import {
  type CSSProperties,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  CoinsIcon,
  PawPrintIcon,
  ShoppingBagIcon,
  UsersIcon,
  XIcon,
} from "lucide-react";

import treasuryChestImage from "../../assets/staffer-pets/treasury-chest.png";
import { Column } from "../Column";
import { Row } from "../Row";
import { Text } from "../Text";
import { StafferPetIncentiveToken } from "./StafferPetIncentiveToken";
import { StafferPetAdminPage } from "./StafferPetAdminPage";
import { StafferPetGodModeIcon } from "./StafferPetGodModeIcon";
import { PlayyardPondPlants, PlayyardPond } from "./StafferPetPlayyardDecor";
import { PlayyardLandLayer } from "./playyard/PlayyardLandLayer";
import { PlayyardLightSnow } from "./playyard/PlayyardLightSnow";
import { PlayyardNightLights } from "./playyard/PlayyardNightLights";
import { PlayyardPetCard } from "./playyard/PlayyardPetCard";
import { PlayyardPuddles } from "./playyard/PlayyardPuddles";
import { PlayyardSkyLayer } from "./playyard/PlayyardSkyLayer";
import { PlayyardSkyStatus } from "./playyard/PlayyardSkyStatus";
import { PlayyardSnowDrifts } from "./playyard/PlayyardSnowDrifts";
import { PlayyardWeatherLayer } from "./playyard/PlayyardWeatherLayer";
import { StafferPetProfileSprite } from "./StafferPetProfileSprite";
import { StafferPetTransactionsPage } from "./StafferPetTransactionsPage";
import { StafferPetWeeklyCoinReportPage } from "./StafferPetWeeklyCoinReportPage";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";

import { useGetPetTransactions } from "@/hooks/queries/useGetPetTransactions";
import {
  type WeeklyCoinReport,
  type WeeklyCoinReportPeriod,
  useGetWeeklyCoinReport,
} from "@/hooks/queries/useGetWeeklyCoinReport";
import { usePlayyardSky } from "@/hooks/usePlayyardSky";
import { useStafferPet } from "../../hooks/useStafferPets";
import {
  EMPTY_PLAYYARD_SKY_PREVIEW,
  IS_PLAYYARD_WEATHER_ENABLED,
} from "@/lib/constants";
import { dates } from "../../lib/dates";
import { cn } from "../../lib/utils";
import type {
  Pet,
  PetTransaction,
  PlayyardSkyPreview,
  User,
} from "../../types";

const BACK_ROW_PET_SIZE_PX = 64;
const MIDDLE_ROW_PET_SIZE_PX = 78;
const FRONT_ROW_PET_SIZE_PX = 92;
const MIN_HOP_BLOCK_DURATION_MS = 7_000;
const MAX_HOP_BLOCK_DURATION_MS = 12_000;
const MIN_TIME_BETWEEN_HOPS_MS = 3_000;
const HOP_BLOCK_EDGE_BUFFER_MS = 500;
const HOP_CLUSTER_SIZE = 3;
const HOP_DURATION_MS = 450;
const BALL_NUDGE_DURATION_MS = 900;
const PET_WANDER_DURATION_MS = 450;
const PET_WANDER_STEP_PX = 24;
const MAX_PET_WANDER_OFFSET_PX = 48;
const PET_REACTION_DURATION_MS = 1_100;
const PET_REACTION_SYMBOLS = ["♥", "✦", "!"] as const;
const PET_GOD_EMAIL = "tyler@laborup.com";
const CALM_SWAY = { duration: "3s", angle: "4deg" };
const WINDY_SWAY = { duration: "1.4s", angle: "9deg" };
const COIN_LEADERBOARD_EXCLUDED_EMAILS = new Set([
  "tyler@laborup.com",
  "simba@laborup.com",
]);

type PetReactionSymbol = (typeof PET_REACTION_SYMBOLS)[number];

const getRandomInteger = (minimum: number, maximum: number) =>
  Math.floor(Math.random() * (maximum - minimum + 1)) + minimum;

const getShuffledItems = <T,>(items: T[]) => {
  const shuffledItems = [...items];
  for (let index = shuffledItems.length - 1; index > 0; index -= 1) {
    const randomIndex = getRandomInteger(0, index);
    [shuffledItems[index], shuffledItems[randomIndex]] = [
      shuffledItems[randomIndex],
      shuffledItems[index],
    ];
  }
  return shuffledItems;
};

export const StafferPetPlayyard = ({
  open,
  onAdopt,
  onOpenChange,
  onShop,
}: {
  open: boolean;
  onAdopt: () => void;
  onOpenChange: (open: boolean) => void;
  onShop: () => void;
}) => {
  const {
    coinBalance,
    currentUser,
    pet: currentUserPet,
    pets,
    sameCompanyUsers,
    unlockedColorIds,
  } = useStafferPet();
  // The current user's ledger, newest first, for the popover + transactions page.
  const { data: coinLedgerData } = useGetPetTransactions(currentUser?.id);
  const [weeklyCoinReportPeriod, setWeeklyCoinReportPeriod] =
    useState<WeeklyCoinReportPeriod>("THIS_WEEK");
  const weeklyCoinReportQuery = useGetWeeklyCoinReport(weeklyCoinReportPeriod);
  const coinLedger = coinLedgerData ?? [];
  const isPetGod = currentUser?.email.toLowerCase() === PET_GOD_EMAIL;

  /* React Hooks */
  const [hoppingUserIds, setHoppingUserIds] = useState<Set<number>>(
    () => new Set(),
  );
  const [isBallNudging, setIsBallNudging] = useState(false);
  const [isTransactionsOpen, setIsTransactionsOpen] = useState(false);
  const [isAdminOpen, setIsAdminOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [skyPreview, setSkyPreview] = useState<PlayyardSkyPreview>(
    EMPTY_PLAYYARD_SKY_PREVIEW,
  );
  const { sky, weather, now } = usePlayyardSky({
    isOpen: open,
    preview: skyPreview,
  });
  const [isWeeklyCoinReportOpen, setIsWeeklyCoinReportOpen] = useState(false);
  const [ballNudgeCount, setBallNudgeCount] = useState(0);
  const [shuffledYardUserIds, setShuffledYardUserIds] = useState<number[]>([]);
  // One state object so the mid-wander pet and the settled offsets can never
  // drift apart: the wander commits into `offsets` when the animation ends.
  const [wanderState, setWanderState] = useState<{
    offsets: Record<number, number>;
    wandering: { userId: number; direction: "left" | "right" } | null;
  }>({ offsets: {}, wandering: null });
  const [activeReaction, setActiveReaction] = useState<{
    userId: number;
    symbol: PetReactionSymbol;
    sequence: number;
  } | null>(null);
  const hopEndTimeoutIdsRef = useRef(
    new Map<number, ReturnType<typeof setTimeout>>(),
  );
  const ballNudgeTimeoutIdRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const wanderTimeoutIdRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reactionTimeoutIdRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const reactionSequenceRef = useRef(0);
  const wasPlayyardOpenRef = useRef(false);
  const visibleUserPets = useMemo(() => {
    const petByUserId = new Map((pets ?? []).map((pet) => [pet.userId, pet]));
    return (sameCompanyUsers ?? [])
      .flatMap((user) => {
        if (user.deletedAt !== null) return [];
        const pet = petByUserId.get(user.id);
        if (!pet || pet.shouldHide) return [];
        return [{ ...user, pet }];
      })
      .sort((left, right) => left.firstName.localeCompare(right.firstName));
  }, [pets, sameCompanyUsers]);
  const usersByCoinBalance = useMemo(
    () =>
      visibleUserPets
        .map((user) => ({
          ...user,
          coinBalance: user.pet.coinBalance,
        }))
        .filter(
          (user) =>
            user.coinBalance > 0 &&
            !COIN_LEADERBOARD_EXCLUDED_EMAILS.has(user.email),
        )
        .sort(
          (left, right) =>
            right.coinBalance - left.coinBalance ||
            left.firstName.localeCompare(right.firstName),
        ),
    [visibleUserPets],
  );
  useEffect(() => {
    if (!open) {
      wasPlayyardOpenRef.current = false;
      return;
    }

    const yardUserIds = visibleUserPets.map((user) => user.id);
    setShuffledYardUserIds((currentIds) => {
      if (!wasPlayyardOpenRef.current) return getShuffledItems(yardUserIds);

      const yardUserIdSet = new Set(yardUserIds);
      const retainedIds = currentIds.filter((userId) =>
        yardUserIdSet.has(userId),
      );
      const retainedIdSet = new Set(retainedIds);
      const newIds = getShuffledItems(
        yardUserIds.filter((userId) => !retainedIdSet.has(userId)),
      );
      return [...retainedIds, ...newIds];
    });
    wasPlayyardOpenRef.current = true;
  }, [open, visibleUserPets]);
  const playyardRows = useMemo(() => {
    const currentUserEntry = visibleUserPets.find(
      (user) =>
        user.id === currentUser?.id || user.email === currentUser?.email,
    );
    const shuffledUserIndex = new Map(
      shuffledYardUserIds.map((userId, index) => [userId, index]),
    );
    const otherUsers = visibleUserPets
      .filter(
        (user) =>
          user.id !== currentUserEntry?.id &&
          user.email !== currentUserEntry?.email,
      )
      .sort(
        (left, right) =>
          (shuffledUserIndex.get(left.id) ?? Number.MAX_SAFE_INTEGER) -
          (shuffledUserIndex.get(right.id) ?? Number.MAX_SAFE_INTEGER),
      );

    if (visibleUserPets.length <= 6) {
      return {
        back: [],
        middle: [],
        front: currentUserEntry
          ? [currentUserEntry, ...otherUsers]
          : otherUsers,
      };
    }

    if (visibleUserPets.length <= 12) {
      const frontCount = Math.ceil(visibleUserPets.length / 2);
      const middleCount = visibleUserPets.length - frontCount;
      return {
        back: [],
        middle: otherUsers.slice(0, middleCount),
        front: currentUserEntry
          ? [currentUserEntry, ...otherUsers.slice(middleCount)]
          : otherUsers.slice(middleCount),
      };
    }

    const frontCount = Math.ceil(visibleUserPets.length / 3);
    const remainingCount = visibleUserPets.length - frontCount;
    const middleCount = Math.ceil(remainingCount / 2);
    const backCount = remainingCount - middleCount;
    return {
      back: otherUsers.slice(0, backCount),
      middle: otherUsers.slice(backCount, backCount + middleCount),
      front: currentUserEntry
        ? [currentUserEntry, ...otherUsers.slice(backCount + middleCount)]
        : otherUsers.slice(backCount + middleCount),
    };
  }, [currentUser, shuffledYardUserIds, visibleUserPets]);
  const handlePetHop = useCallback((userId: number) => {
    const existingTimeoutId = hopEndTimeoutIdsRef.current.get(userId);
    if (existingTimeoutId) clearTimeout(existingTimeoutId);

    setHoppingUserIds((currentIds) => new Set(currentIds).add(userId));
    const timeoutId = setTimeout(() => {
      setHoppingUserIds((currentIds) => {
        const nextIds = new Set(currentIds);
        nextIds.delete(userId);
        return nextIds;
      });
      hopEndTimeoutIdsRef.current.delete(userId);
    }, HOP_DURATION_MS);
    hopEndTimeoutIdsRef.current.set(userId, timeoutId);
  }, []);
  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) setIsTransactionsOpen(false);
    onOpenChange(isOpen);
  };
  const handleBallClick = useCallback(() => {
    const nearestFrontPet = playyardRows.front[playyardRows.front.length - 1];
    if (nearestFrontPet) handlePetHop(nearestFrontPet.id);

    if (ballNudgeTimeoutIdRef.current) {
      clearTimeout(ballNudgeTimeoutIdRef.current);
    }
    setIsBallNudging(true);
    setBallNudgeCount((currentCount) => currentCount + 1);
    ballNudgeTimeoutIdRef.current = setTimeout(() => {
      setIsBallNudging(false);
      ballNudgeTimeoutIdRef.current = null;
    }, BALL_NUDGE_DURATION_MS);
  }, [handlePetHop, playyardRows.front]);
  const handlePetWander = useCallback((userId: number) => {
    if (wanderTimeoutIdRef.current) {
      clearTimeout(wanderTimeoutIdRef.current);
    }

    const randomDirection: "left" | "right" =
      Math.random() < 0.5 ? "left" : "right";
    setWanderState((current) => {
      const currentOffset = current.offsets[userId] ?? 0;
      let direction = randomDirection;
      if (currentOffset <= -MAX_PET_WANDER_OFFSET_PX) direction = "right";
      if (currentOffset >= MAX_PET_WANDER_OFFSET_PX) direction = "left";
      return { ...current, wandering: { userId, direction } };
    });
    wanderTimeoutIdRef.current = setTimeout(() => {
      setWanderState((current) => {
        if (current.wandering === null) return current;

        const offsetChange =
          current.wandering.direction === "left"
            ? -PET_WANDER_STEP_PX
            : PET_WANDER_STEP_PX;
        const wanderingUserId = current.wandering.userId;
        return {
          offsets: {
            ...current.offsets,
            [wanderingUserId]:
              (current.offsets[wanderingUserId] ?? 0) + offsetChange,
          },
          wandering: null,
        };
      });
      wanderTimeoutIdRef.current = null;
    }, PET_WANDER_DURATION_MS);
  }, []);
  const handlePetClick = useCallback(
    (userId: number) => {
      handlePetHop(userId);
      setSelectedUserId(userId);
      if (reactionTimeoutIdRef.current) {
        clearTimeout(reactionTimeoutIdRef.current);
      }

      reactionSequenceRef.current += 1;
      const symbol =
        PET_REACTION_SYMBOLS[
          getRandomInteger(0, PET_REACTION_SYMBOLS.length - 1)
        ];
      setActiveReaction({
        userId,
        symbol,
        sequence: reactionSequenceRef.current,
      });
      reactionTimeoutIdRef.current = setTimeout(() => {
        setActiveReaction(null);
        reactionTimeoutIdRef.current = null;
      }, PET_REACTION_DURATION_MS);
    },
    [handlePetHop],
  );

  useEffect(() => {
    if (!open || visibleUserPets.length === 0) {
      hopEndTimeoutIdsRef.current.forEach((timeoutId) => {
        clearTimeout(timeoutId);
      });
      hopEndTimeoutIdsRef.current.clear();
      if (ballNudgeTimeoutIdRef.current) {
        clearTimeout(ballNudgeTimeoutIdRef.current);
        ballNudgeTimeoutIdRef.current = null;
      }
      if (wanderTimeoutIdRef.current) {
        clearTimeout(wanderTimeoutIdRef.current);
        wanderTimeoutIdRef.current = null;
      }
      if (reactionTimeoutIdRef.current) {
        clearTimeout(reactionTimeoutIdRef.current);
        reactionTimeoutIdRef.current = null;
      }
      setHoppingUserIds(new Set());
      setIsBallNudging(false);
      setActiveReaction(null);
      setSelectedUserId(null);
      setWanderState({ offsets: {}, wandering: null });
      return;
    }

    const userIds = visibleUserPets.map((user) => user.id);
    const timeoutIds = new Set<ReturnType<typeof setTimeout>>();

    const scheduleTimeout = (callback: () => void, delayMs: number) => {
      const timeoutId = setTimeout(() => {
        timeoutIds.delete(timeoutId);
        callback();
      }, delayMs);
      timeoutIds.add(timeoutId);
    };

    const scheduleBlock = () => {
      const blockDurationMs = getRandomInteger(
        MIN_HOP_BLOCK_DURATION_MS,
        MAX_HOP_BLOCK_DURATION_MS,
      );
      const availableUserIds = [...userIds];
      const selectedUserIds: number[] = [];
      while (
        availableUserIds.length > 0 &&
        selectedUserIds.length < HOP_CLUSTER_SIZE
      ) {
        const selectedIndex = getRandomInteger(0, availableUserIds.length - 1);
        const [selectedUserId] = availableUserIds.splice(selectedIndex, 1);
        selectedUserIds.push(selectedUserId);
      }

      let previousHopDelayMs = 0;
      selectedUserIds.forEach((userId, index) => {
        const remainingHopCount = selectedUserIds.length - index - 1;
        const earliestHopDelayMs =
          index === 0
            ? HOP_BLOCK_EDGE_BUFFER_MS
            : previousHopDelayMs + MIN_TIME_BETWEEN_HOPS_MS;
        const latestHopDelayMs =
          blockDurationMs -
          HOP_BLOCK_EDGE_BUFFER_MS -
          remainingHopCount * MIN_TIME_BETWEEN_HOPS_MS;
        const hopDelayMs = getRandomInteger(
          earliestHopDelayMs,
          latestHopDelayMs,
        );
        scheduleTimeout(() => {
          handlePetWander(userId);
        }, hopDelayMs);
        previousHopDelayMs = hopDelayMs;
      });
      scheduleTimeout(scheduleBlock, blockDurationMs);
    };

    scheduleBlock();

    return () => {
      timeoutIds.forEach((timeoutId) => {
        clearTimeout(timeoutId);
      });
    };
  }, [handlePetWander, open, visibleUserPets]);

  /* Helpers */
  const selectedUser = visibleUserPets.find(
    (user) => user.id === selectedUserId,
  );
  const selectedUserRankIndex = usersByCoinBalance.findIndex(
    (user) => user.id === selectedUserId,
  );
  const getSceneStyle = (): CSSProperties => {
    const sway = sky.isWindy ? WINDY_SWAY : CALM_SWAY;
    return {
      ...sky.vars,
      "--yard-sway-duration": sway.duration,
      "--yard-sway-angle": sway.angle,
    } as CSSProperties;
  };

  /* Render */
  const renderPlayyard = () => {
    if (sameCompanyUsers === undefined) {
      return (
        <Column className="relative z-40 h-full items-center justify-center">
          <Text className="font-mono text-xs font-black uppercase tracking-[0.18em] text-slate-700">
            Opening the gate…
          </Text>
        </Column>
      );
    }

    if (visibleUserPets.length === 0) {
      return (
        <Column className="relative z-40 h-full items-center justify-center gap-4 px-6 text-center">
          <Column className="h-16 w-16 items-center justify-center border-4 border-slate-900 bg-orange-100 shadow-[4px_4px_0_#172033]">
            <PawPrintIcon className="h-8 w-8 text-orange-600" />
          </Column>
          <Column className="gap-1">
            <Text className="font-mono text-sm font-black uppercase tracking-[0.14em] text-slate-900">
              The yard is waiting
            </Text>
            <Text className="font-mono text-[10px] font-bold uppercase tracking-[0.1em] text-slate-600">
              Adopt the first pet and open the gates
            </Text>
          </Column>
        </Column>
      );
    }

    return (
      <>
        <PlayyardPetRow
          activeReaction={activeReaction}
          className="bottom-[39%] left-[35%] right-[5%] z-10 brightness-[.96] saturate-[.85]"
          hoppingUserIds={hoppingUserIds}
          onPetClick={handlePetClick}
          petSizePx={BACK_ROW_PET_SIZE_PX}
          petHorizontalOffsets={wanderState.offsets}
          users={playyardRows.back}
          wanderingPet={wanderState.wandering}
        />
        <PlayyardPetRow
          activeReaction={activeReaction}
          className="inset-x-[4%] bottom-[22%] z-20"
          hoppingUserIds={hoppingUserIds}
          onPetClick={handlePetClick}
          petSizePx={MIDDLE_ROW_PET_SIZE_PX}
          petHorizontalOffsets={wanderState.offsets}
          users={playyardRows.middle}
          wanderingPet={wanderState.wandering}
        />
        <PlayyardPetRow
          activeReaction={activeReaction}
          className="inset-x-[3%] bottom-[3%] z-30"
          hoppingUserIds={hoppingUserIds}
          onPetClick={handlePetClick}
          petSizePx={FRONT_ROW_PET_SIZE_PX}
          petHorizontalOffsets={wanderState.offsets}
          users={playyardRows.front}
          wanderingPet={wanderState.wandering}
        />
      </>
    );
  };

  const renderCollectionSummary = () => {
    if (currentUserPet === null) {
      return "Adopt a pet to join the playyard";
    }

    const colorCount = unlockedColorIds.length;
    const colorLabel = colorCount === 1 ? "color" : "colors";
    return `${colorCount.toString()} ${colorLabel} unlocked`;
  };

  const renderContent = () => {
    if (isAdminOpen && isPetGod) {
      return (
        <StafferPetAdminPage
          currentUserId={currentUser.id}
          pets={pets ?? []}
          users={sameCompanyUsers ?? []}
          onBack={() => {
            setIsAdminOpen(false);
          }}
        />
      );
    }
    if (isWeeklyCoinReportOpen) {
      return (
        <StafferPetWeeklyCoinReportPage
          report={weeklyCoinReportQuery.data}
          period={weeklyCoinReportPeriod}
          onPeriodChange={setWeeklyCoinReportPeriod}
          onBack={() => {
            setIsWeeklyCoinReportOpen(false);
          }}
        />
      );
    }
    if (isTransactionsOpen) {
      return (
        <StafferPetTransactionsPage
          coinBalance={coinBalance}
          coinLedger={coinLedger}
          onBack={() => {
            setIsTransactionsOpen(false);
          }}
        />
      );
    }

    return (
      <>
        <DialogHeader className="border-b-4 border-slate-900 pb-3 text-left">
          <Row className="w-full items-center justify-between gap-4">
            <Column className="gap-0">
              <DialogTitle className="font-mono text-xl font-black uppercase tracking-[0.1em] text-slate-900">
                Winterfell Playyard
              </DialogTitle>
              <DialogDescription className="font-mono text-xs uppercase tracking-[0.18em] text-slate-500">
                {visibleUserPets.length.toString()} companions in the North
              </DialogDescription>
            </Column>
            <Row className="items-center gap-3">
              <Column className="group relative">
                <Row className="items-center gap-2 border-2 border-slate-900 bg-white px-3 py-2 shadow-[3px_3px_0_#172033]">
                  <UsersIcon className="h-4 w-4 text-violet-600" />
                  <Text className="font-mono text-sm font-black text-slate-900">
                    {visibleUserPets.length}
                  </Text>
                </Row>
                <Column className="pointer-events-none invisible absolute right-0 top-full z-50 w-64 pt-2 opacity-0 transition-opacity group-hover:pointer-events-auto group-hover:visible group-hover:opacity-100">
                  <Column className="gap-2 border-2 border-slate-900 bg-[#fffaf0] p-3 shadow-[3px_3px_0_#172033]">
                    <Text className="border-b-2 border-dashed border-slate-300 pb-2 font-mono text-[10px] font-black uppercase tracking-[0.1em] text-slate-900">
                      Top coin balances
                    </Text>
                    <Column className="max-h-60 gap-2 overflow-y-auto overscroll-contain pr-1">
                      {usersByCoinBalance.map((user) => (
                        <Row
                          key={user.id}
                          className="items-center justify-between gap-4"
                        >
                          <Text className="truncate font-mono text-[9px] font-bold uppercase tracking-[0.06em] text-slate-700">
                            {user.firstName} {user.lastName}
                          </Text>
                          <Row className="shrink-0 items-center gap-2">
                            {user.pet.incentiveTokensAvailable > 0 ? (
                              <Row className="items-center gap-1">
                                <StafferPetIncentiveToken sizePx={12} />
                                <Text className="font-mono text-[9px] font-black text-violet-700">
                                  {user.pet.incentiveTokensAvailable.toLocaleString(
                                    "en-US",
                                  )}
                                </Text>
                              </Row>
                            ) : null}
                            <Row className="items-center gap-1">
                              <CoinsIcon className="h-3 w-3 text-amber-500" />
                              <Text className="font-mono text-[9px] font-black text-slate-900">
                                {user.coinBalance.toLocaleString("en-US")}
                              </Text>
                            </Row>
                          </Row>
                        </Row>
                      ))}
                    </Column>
                  </Column>
                </Column>
              </Column>
              <WeeklyCoinReportPopover
                report={weeklyCoinReportQuery.data}
                isLoading={weeklyCoinReportQuery.isLoading}
                onOpen={() => {
                  setIsWeeklyCoinReportOpen(true);
                }}
              />
              <CoinLedgerPopover
                coinBalance={coinBalance}
                coinLedger={coinLedger}
                onSeeAll={() => {
                  setIsTransactionsOpen(true);
                }}
              />
              {isPetGod ? (
                <Button
                  type="button"
                  variant="outlineDark"
                  onClick={() => {
                    setIsAdminOpen(true);
                  }}
                  className="h-9 rounded-none border-2 border-slate-900 bg-violet-200 px-2 shadow-[3px_3px_0_#172033]"
                >
                  <StafferPetGodModeIcon />
                  <Text className="sr-only">Pet God Mode</Text>
                </Button>
              ) : null}
              <DialogClose asChild>
                <Button
                  type="button"
                  variant="ghost"
                  className="h-8 w-8 shrink-0 p-0 text-slate-900 hover:bg-orange-100"
                >
                  <XIcon />
                  <Text className="sr-only">Close</Text>
                </Button>
              </DialogClose>
            </Row>
          </Row>
        </DialogHeader>

        <TooltipProvider delayDuration={150}>
          <Column
            className="relative min-h-0 overflow-hidden border-4 border-slate-900 shadow-[6px_6px_0_#172033] [container-type:inline-size]"
            style={getSceneStyle()}
          >
            <PlayyardSkyLayer sky={sky} />
            <PlayyardLandLayer sky={sky} />
            <PlayyardPond />
            <PlayyardPondPlants />
            {sky.isWet ? <PlayyardPuddles /> : null}
            <PlayyardBall
              key={ballNudgeCount}
              isNudging={isBallNudging}
              onClick={handleBallClick}
            />
            <PlayyardFamilyCorner />
            {renderPlayyard()}
            <PlayyardSnowDrifts />
            {/* Multiplies the time-of-day tint over pets and scenery alike.
                Daytime has no tint, so skip the full-scene blend entirely. */}
            {sky.nightAmount > 0 ? (
              <Column
                className="pointer-events-none absolute inset-0 z-[38] mix-blend-multiply"
                style={{ background: "var(--yard-tint)" }}
              />
            ) : null}
            {IS_PLAYYARD_WEATHER_ENABLED ? (
              <PlayyardWeatherLayer sky={sky} />
            ) : null}
            <PlayyardLightSnow />
            <PlayyardNightLights sky={sky} />
            {selectedUser ? (
              <PlayyardPetCard
                user={selectedUser}
                rank={
                  selectedUserRankIndex === -1
                    ? null
                    : selectedUserRankIndex + 1
                }
                rankedCount={usersByCoinBalance.length}
                onClose={() => {
                  setSelectedUserId(null);
                }}
              />
            ) : null}
            <PlayyardSkyStatus
              sky={sky}
              weather={weather}
              now={now}
              preview={skyPreview}
              isAdmin={isPetGod}
              setPreview={setSkyPreview}
            />
          </Column>
        </TooltipProvider>

        <Row className="items-center justify-between gap-4 border-t-4 border-slate-900 pt-4">
          <Column className="min-w-0 gap-0">
            <Text className="font-mono text-[9px] font-black uppercase tracking-[0.12em] text-slate-500">
              Your colors
            </Text>
            <Text className="truncate font-mono text-xs font-black uppercase tracking-[0.1em] text-slate-900">
              {renderCollectionSummary()}
            </Text>
          </Column>
          {/* The shop now covers pet changes too, so an existing pet only
              needs one door; adoption stays for first-timers. */}
          <Button
            type="button"
            variant="outlineDark"
            onClick={currentUserPet ? onShop : onAdopt}
            className="h-12 shrink-0 border-2 border-slate-900 bg-slate-900 px-5 font-mono text-xs font-black uppercase tracking-[0.12em] text-orange-50 shadow-[3px_3px_0_#f97316] disabled:opacity-100"
          >
            {currentUserPet ? (
              <ShoppingBagIcon className="mr-2 h-4 w-4" />
            ) : (
              <PawPrintIcon className="mr-2 h-4 w-4" />
            )}
            {currentUserPet ? "Shop & Customize" : "Adopt a pet"}
          </Button>
        </Row>
      </>
    );
  };

  const render = () => {
    return (
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent
          data-pet-yard=""
          hideCloseButton
          className="staffer-pet-pixel-modal grid h-[min(92vh,780px)] w-[min(96vw,1180px)] max-w-none grid-rows-[auto_minmax(0,1fr)_auto] gap-4 rounded-none border-4 border-slate-900 bg-[#edf3f8] px-6 pb-6 pt-4 sm:rounded-none"
        >
          {renderContent()}
        </DialogContent>
      </Dialog>
    );
  };

  return render();
};

const PlayyardPetRow = ({
  activeReaction,
  className,
  hoppingUserIds,
  onPetClick,
  petHorizontalOffsets,
  petSizePx,
  users,
  wanderingPet,
}: {
  activeReaction: {
    userId: number;
    symbol: PetReactionSymbol;
    sequence: number;
  } | null;
  className: string;
  hoppingUserIds: Set<number>;
  onPetClick: (userId: number) => void;
  petHorizontalOffsets: Record<number, number>;
  petSizePx: number;
  users: (User & { pet: Pet })[];
  wanderingPet: {
    userId: number;
    direction: "left" | "right";
  } | null;
}) => {
  /* Render */
  const render = () => {
    if (users.length === 0) return null;

    return (
      <Column className={cn("pointer-events-none absolute h-24", className)}>
        <div
          className="absolute inset-x-0 bottom-0 grid items-end justify-items-center gap-1"
          style={{
            gridTemplateColumns: `repeat(${users.length.toString()}, minmax(0, 1fr))`,
          }}
        >
          {users.map((user) => {
            let wanderClassName: string | undefined;
            if (
              wanderingPet?.userId === user.id &&
              wanderingPet.direction === "left"
            ) {
              wanderClassName = "staffer-pet-playyard-wander-left";
            }
            if (
              wanderingPet?.userId === user.id &&
              wanderingPet.direction === "right"
            ) {
              wanderClassName = "staffer-pet-playyard-wander-right";
            }
            return (
              <Tooltip key={user.id}>
                <TooltipTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => {
                      onPetClick(user.id);
                    }}
                    className="pointer-events-auto relative h-auto w-auto rounded-none p-0 hover:!bg-transparent dark:hover:!bg-transparent"
                    style={{
                      transform: `translateX(${(petHorizontalOffsets[user.id] ?? 0).toString()}px)`,
                    }}
                  >
                    {activeReaction?.userId === user.id ? (
                      <PlayyardPetReaction
                        key={activeReaction.sequence}
                        symbol={activeReaction.symbol}
                      />
                    ) : null}
                    <StafferPetProfileSprite
                      pet={user.pet}
                      sizePx={petSizePx}
                      className={cn(
                        wanderClassName,
                        hoppingUserIds.has(user.id) &&
                          "staffer-pet-playyard-hop",
                      )}
                    />
                  </Button>
                </TooltipTrigger>
                <TooltipContent
                  side="top"
                  sideOffset={4}
                  className="rounded-none border-2 border-slate-900 bg-[#fff4cf] px-2 py-1 font-mono text-[9px] font-black uppercase tracking-[0.1em] text-slate-900 shadow-[2px_2px_0_#172033]"
                >
                  {user.firstName}
                </TooltipContent>
              </Tooltip>
            );
          })}
        </div>
      </Column>
    );
  };

  return render();
};

const PlayyardPetReaction = ({ symbol }: { symbol: PetReactionSymbol }) => {
  /* Render */
  const render = () => {
    return (
      <Column className="pointer-events-none absolute -top-10 left-1/2 z-30 -translate-x-1/2">
        <Column className="staffer-pet-playyard-reaction relative h-7 w-7 items-center justify-center border-2 border-slate-900 bg-white shadow-[2px_2px_0_#172033]">
          <Text
            className={cn(
              "font-mono text-sm font-black leading-none",
              symbol === "♥" && "text-rose-500",
              symbol === "✦" && "text-violet-500",
              symbol === "!" && "text-amber-500",
            )}
          >
            {symbol}
          </Text>
          <Column className="absolute -bottom-1.5 left-2 h-1.5 w-1.5 border-b-2 border-r-2 border-slate-900 bg-white" />
        </Column>
      </Column>
    );
  };

  return render();
};

const PlayyardBall = ({
  isNudging,
  onClick,
}: {
  isNudging: boolean;
  onClick: () => void;
}) => {
  /* Render */
  const render = () => {
    return (
      <Button
        type="button"
        variant="ghost"
        onClick={onClick}
        className={cn(
          "absolute bottom-[5%] right-[3%] z-[36] h-8 w-8 cursor-pointer rounded-none bg-slate-900 p-0 hover:bg-slate-900",
          isNudging && "staffer-pet-playyard-ball-nudge",
        )}
        style={{
          clipPath:
            "polygon(25% 0, 75% 0, 100% 25%, 100% 75%, 75% 100%, 25% 100%, 0 75%, 0 25%)",
        }}
      >
        <Column
          className="absolute inset-1 bg-orange-500"
          style={{
            clipPath:
              "polygon(25% 0, 75% 0, 100% 25%, 100% 75%, 75% 100%, 25% 100%, 0 75%, 0 25%)",
          }}
        />
        <Column className="absolute left-2 top-1.5 h-1.5 w-1.5 bg-orange-200" />
      </Button>
    );
  };

  return render();
};

const PlayyardFamilyCorner = () => {
  /* Render */
  const render = () => {
    return (
      <Row className="absolute bottom-0 left-0 h-14 w-16 items-end justify-start gap-1">
        <PlayyardFlower petalColor="#e9f3fc" />
        <PlayyardFlower petalColor="#c3d9eb" />
        <PlayyardWren />
      </Row>
    );
  };

  return render();
};

const PlayyardFlower = ({ petalColor }: { petalColor: string }) => {
  /* Render */
  const render = () => {
    return (
      <Column className="relative h-10 w-7">
        <Column className="absolute bottom-0 left-3 h-6 w-1 bg-[#397d48]" />
        <Column className="absolute bottom-2 left-1.5 h-1.5 w-2 bg-[#55a65a]" />
        <Column className="absolute left-2.5 top-0 h-2 w-2 bg-amber-300" />
        <Column
          className="absolute left-0.5 top-1.5 h-2 w-2"
          style={{ backgroundColor: petalColor }}
        />
        <Column
          className="absolute right-0.5 top-1.5 h-2 w-2"
          style={{ backgroundColor: petalColor }}
        />
        <Column
          className="absolute left-2.5 top-3.5 h-2 w-2"
          style={{ backgroundColor: petalColor }}
        />
      </Column>
    );
  };

  return render();
};

const PlayyardWren = () => {
  /* Render */
  const render = () => {
    return (
      <Column className="absolute bottom-9 left-1/2 h-5 w-7 -translate-x-1/2">
        <Column className="absolute left-0 top-2 h-1.5 w-2 bg-[#754126]" />
        <Column className="absolute left-1.5 top-1.5 h-2.5 w-3.5 bg-[#9a5b35]" />
        <Column className="absolute left-2 top-0 h-2 w-2 bg-[#b96f42]" />
        <Column className="absolute bottom-0 left-2 h-2 w-2 bg-[#8b4f2f]" />
        <Column className="absolute right-1 top-1 h-3 w-2.5 bg-[#a9643c]" />
        <Column className="absolute right-0 top-2 h-1 w-1 bg-amber-500" />
      </Column>
    );
  };

  return render();
};

const TreasuryChestIcon = () => {
  /* Render */
  const render = () => {
    return (
      <img
        src={treasuryChestImage}
        alt=""
        className="h-5 w-5 shrink-0 object-contain [image-rendering:pixelated]"
      />
    );
  };

  return render();
};

const WeeklyCoinReportPopover = ({
  report,
  isLoading,
  onOpen,
}: {
  report: WeeklyCoinReport | undefined;
  isLoading: boolean;
  onOpen: () => void;
}) => {
  const headlineCategories = [
    ["PLACEMENT", "Placements"],
    ["SEARCH_SUBMIT", "Search submits"],
    ["STANDARD_SUBMIT", "Submits"],
    ["SEARCH_SHORTLIST", "Shortlists"],
    ["STREAK_COINS", "Streak coins"],
  ] as const;
  const weekLabel = report
    ? `${dates.formatDateDivider(report.weekStart)} – ${dates.formatDateDivider(
        new Date(new Date(report.weekEnd).getTime() - 1).toISOString(),
      )}`
    : null;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outlineDark"
          onClick={onOpen}
          className="h-auto items-center gap-2 rounded-none border-2 border-slate-900 bg-amber-300 px-3 py-2 text-slate-900 shadow-[3px_3px_0_#172033] hover:bg-amber-200"
        >
          <TreasuryChestIcon />
          <Text className="font-mono text-sm font-black text-slate-900">
            Treasury
          </Text>
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-80 border-2 border-slate-900 bg-[#fffaf0] p-3 shadow-[4px_4px_0_#172033]"
      >
        <Column className="gap-3">
          <Column className="gap-0 border-b-2 border-dashed border-slate-300 pb-2">
            <Text className="font-mono text-[11px] font-black uppercase tracking-[0.1em] text-slate-900">
              The Treasury
            </Text>
            <Text className="font-mono text-[9px] font-bold uppercase tracking-[0.06em] text-orange-600">
              {weekLabel ?? "Loading completed week…"}
            </Text>
          </Column>
          {isLoading || report === undefined ? (
            <Text className="font-mono text-[10px] font-bold uppercase tracking-[0.06em] text-slate-500">
              Counting coins…
            </Text>
          ) : (
            <>
              <Row className="items-center justify-between border-2 border-slate-900 bg-amber-100 px-2 py-2">
                <Text className="font-mono text-[10px] font-black uppercase tracking-[0.08em] text-slate-900">
                  Team haul
                </Text>
                <Row className="items-center gap-1">
                  <CoinsIcon className="h-3.5 w-3.5 text-amber-600" />
                  <Text className="font-mono text-sm font-black text-slate-900">
                    {report.totalCoinsEarned.toLocaleString("en-US")}
                  </Text>
                </Row>
              </Row>
              <Column className="gap-2">
                {headlineCategories.map(([category, label]) => {
                  const earned = report.categoryTotals[category] ?? 0;
                  if (earned === 0) return null;
                  const leaders = report.staffers.filter(
                    (staffer) => staffer.categoryTotals[category] === earned,
                  );
                  return (
                    <Row
                      key={category}
                      className="items-center justify-between gap-3"
                    >
                      <Column className="min-w-0 gap-0">
                        <Text className="font-mono text-[9px] font-black uppercase tracking-[0.06em] text-slate-700">
                          {label}
                        </Text>
                        <Text className="truncate font-mono text-[10px] font-bold text-slate-900">
                          {leaders
                            .map((staffer) =>
                              `${staffer.firstName ?? ""} ${staffer.lastName ?? ""}`.trim(),
                            )
                            .join(", ")}
                        </Text>
                      </Column>
                      <Text className="shrink-0 font-mono text-[10px] font-black text-amber-700">
                        {earned.toLocaleString("en-US")}
                      </Text>
                    </Row>
                  );
                })}
              </Column>
            </>
          )}
        </Column>
      </PopoverContent>
    </Popover>
  );
};

const CoinLedgerPopover = ({
  coinBalance,
  coinLedger,
  onSeeAll,
}: {
  coinBalance: number;
  coinLedger: PetTransaction[];
  onSeeAll: () => void;
}) => {
  /* React Hooks */
  // The ledger arrives newest first from the API.
  const recentEarnings = useMemo(
    () =>
      coinLedger
        .filter((transaction) => transaction.type === "INCOME")
        .slice(0, 5),
    [coinLedger],
  );
  const latestSpend = useMemo(
    () =>
      coinLedger.find((transaction) => transaction.type === "PURCHASE") ?? null,
    [coinLedger],
  );

  /* Render */
  const renderRecentEarnings = () => {
    if (recentEarnings.length === 0) return null;

    return (
      <Column
        className={cn(
          "gap-2",
          latestSpend !== null &&
            "border-t-2 border-dashed border-slate-300 pt-3",
        )}
      >
        <Text className="font-mono text-[9px] font-black uppercase tracking-[0.14em] text-emerald-700">
          Recent earnings
        </Text>
        {recentEarnings.map((transaction) => (
          <CoinLedgerEntry key={transaction.id} transaction={transaction} />
        ))}
      </Column>
    );
  };

  const renderLatestSpend = () => {
    if (latestSpend === null) return null;

    return (
      <Column className="gap-2">
        <Text className="font-mono text-[9px] font-black uppercase tracking-[0.14em] text-orange-600">
          Latest purchase
        </Text>
        <CoinLedgerEntry transaction={latestSpend} />
      </Column>
    );
  };

  const renderActivity = () => {
    if (recentEarnings.length === 0 && latestSpend === null) {
      return (
        <Text className="py-3 text-center font-mono text-[10px] font-bold uppercase tracking-[0.1em] text-slate-500">
          No treat activity yet
        </Text>
      );
    }

    return (
      <Column className="gap-3">
        {renderLatestSpend()}
        {renderRecentEarnings()}
      </Column>
    );
  };

  const render = () => {
    return (
      <Popover>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outlineDark"
            className="h-auto items-center gap-2 rounded-none border-2 border-slate-900 bg-amber-300 px-3 py-2 text-slate-900 shadow-[3px_3px_0_#172033] hover:bg-amber-200"
          >
            <CoinsIcon className="h-4 w-4 text-amber-900" />
            <Text className="font-mono text-sm font-black text-slate-900">
              {coinBalance.toLocaleString("en-US")}
            </Text>
            <Text className="font-mono text-[9px] font-black uppercase tracking-[0.12em] text-amber-900">
              Coins
            </Text>
          </Button>
        </PopoverTrigger>
        <PopoverContent
          align="end"
          sideOffset={8}
          className="w-80 rounded-none border-4 border-slate-900 bg-[#fffaf0] p-4 shadow-[5px_5px_0_#172033]"
        >
          <Column className="gap-3">
            <Row className="items-end justify-between gap-3 border-b-4 border-slate-900 pb-3">
              <Column className="gap-0">
                <Text className="font-mono text-[9px] font-black uppercase tracking-[0.14em] text-slate-500">
                  Treat balance
                </Text>
                <Text className="font-mono text-xl font-black text-slate-900">
                  {coinBalance.toLocaleString("en-US")}
                </Text>
              </Column>
              <CoinsIcon className="h-6 w-6 text-amber-500" />
            </Row>
            {renderActivity()}
            <Button
              type="button"
              variant="outlineDark"
              onClick={onSeeAll}
              className="h-10 w-full rounded-none border-2 border-slate-900 bg-slate-900 font-mono text-[10px] font-black uppercase tracking-[0.12em] text-white shadow-[2px_2px_0_#f97316]"
            >
              See all transactions
            </Button>
          </Column>
        </PopoverContent>
      </Popover>
    );
  };

  return render();
};

const CoinLedgerEntry = ({ transaction }: { transaction: PetTransaction }) => {
  /* Helpers */
  const isIncome = transaction.amount > 0;
  const formatAmount = () => {
    const sign = isIncome ? "+" : "−";
    return `${sign}${Math.abs(transaction.amount).toLocaleString("en-US")}`;
  };

  /* Render */
  const render = () => {
    return (
      <Row className="items-center justify-between gap-3 border-2 border-slate-900 bg-white px-3 py-2 shadow-[2px_2px_0_#172033]">
        <Column className="min-w-0 gap-0">
          <Text className="truncate font-mono text-[10px] font-black uppercase tracking-[0.06em] text-slate-900">
            {transaction.description}
          </Text>
          <Text className="font-mono text-[8px] font-bold uppercase tracking-[0.08em] text-slate-500">
            {dates.formatShortDateTime(transaction.createdAt)}
          </Text>
        </Column>
        <Text
          className={cn(
            "shrink-0 font-mono text-xs font-black",
            isIncome ? "text-emerald-700" : "text-orange-600",
          )}
        >
          {formatAmount()}
        </Text>
      </Row>
    );
  };

  return render();
};
